import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModuleNote, NoteCategory } from './module-notes-panel.types';

/**
 * Shared notes panel for dashboard widgets.
 *
 * Presentation only — search, pinned filter, and the inline add form live here;
 * loading, saving, viewing and deleting are the host widget's job, over the
 * shared module notes API. Originally the Bid Room widget's own
 * BidNotesPanelComponent, generalised so the Change Order widget (and any
 * future widget) renders notes identically instead of re-implementing them.
 *
 * `recordNoun` is the only per-module knob — it just personalises the empty
 * state and the add-note placeholder.
 */
@Component({
    selector: 'app-module-notes-panel',
    templateUrl: './module-notes-panel.component.html',
    styleUrls: ['./module-notes-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ModuleNotesPanelComponent {
  @Input() notes: ModuleNote[] = [];
  @Input() isLoading = false;    // fetching this record's notes
  @Input() error: string | null = null;
  @Output() notesChange = new EventEmitter<ModuleNote[]>();
  @Output() onAddNote = new EventEmitter<ModuleNote>();
  @Output() onViewNote = new EventEmitter<ModuleNote>();
  @Output() onEditNote = new EventEmitter<ModuleNote>();
  @Output() onExportNote = new EventEmitter<ModuleNote>();
  @Output() onDeleteNote = new EventEmitter<string>();

  /** Record lock state — locked records stay readable but every write is disabled. */
  @Input() isLocked = false;
  @Input() lockTooltip = 'This record is locked and cannot be edited';

  /** What the record is called in the empty state and add-note placeholder. */
  @Input() recordNoun = 'record';

  filter: NoteCategory | 'All' = 'All';
  isAdding = false;
  newNoteText = '';
  newNoteCategory: NoteCategory = 'General';
  newNotePinned = false;
  searchQuery = '';
  /** Pinned-only quick filter — the one grouping the notes API actually supports. */
  pinnedOnly = false;

  categories: (NoteCategory | 'All')[] = ['All', 'Scope', 'Pricing', 'Risk', 'Meeting', 'General'];
  noteCategories: NoteCategory[] = ['Scope', 'Pricing', 'Risk', 'Meeting', 'General'];

  constructor(private cdr: ChangeDetectorRef) {}

  get pinnedCount(): number {
    return this.notes.filter((n) => n.isPinned).length;
  }

  get filteredNotes(): ModuleNote[] {
    const q = this.searchQuery.trim().toLowerCase();
    return this.notes
      .filter((n) => !this.pinnedOnly || n.isPinned)
      // Search the note body and its author — the two fields the API returns.
      .filter((n) => !q ||
        (n.text || '').toLowerCase().includes(q) ||
        (n.author || '').toLowerCase().includes(q))
      .sort((a, b) => (a.isPinned === b.isPinned ? 0 : a.isPinned ? -1 : 1));
  }

  clearFilters(): void {
    this.searchQuery = '';
    this.pinnedOnly = false;
    this.cdr.markForCheck();
  }

  togglePinnedOnly(): void {
    this.pinnedOnly = !this.pinnedOnly;
    this.cdr.markForCheck();
  }

  handleAddNote(): void {
    if (!this.newNoteText.trim()) return;
    const note: ModuleNote = {
      id: Date.now().toString(),
      text: this.newNoteText,
      category: this.newNoteCategory,
      author: 'Current User',
      authorInitials: 'CU',
      timestamp: 'Just now',
      isPinned: this.newNotePinned
    };
    this.onAddNote.emit(note);
    this.newNoteText = '';
    this.isAdding = false;
    this.cdr.markForCheck();
  }

  handleView(note: ModuleNote): void {
    this.onViewNote.emit(note);
  }

  handleEdit(note: ModuleNote): void {
    this.onEditNote.emit(note);
  }

  handleExport(note: ModuleNote): void {
    this.onExportNote.emit(note);
  }

  handleDelete(id: string): void {
    this.onDeleteNote.emit(id);
  }

  togglePin(id: string): void {
    // Emit updated notes with toggled pin
    const updated = this.notes.map(n => n.id === id ? { ...n, isPinned: !n.isPinned } : n);
    this.notesChange.emit(updated);
  }

  cancelAdd(): void {
    this.isAdding = false;
    this.newNoteText = '';
    this.newNotePinned = false;
    this.newNoteCategory = 'General';
  }

  trackByNoteId(_index: number, note: ModuleNote): string {
    return note.id;
  }
}
